import React, { useState } from 'react';
import { FileText, X, Download, ArrowLeft, ArrowRight } from 'lucide-react';
import type { Item } from '../types/core';
import { AttachmentManager } from '../types/attachments';
import type { LanguageContextType } from '../translations'; 

export interface AttachmentPreviewPanelProps {
  item: Item;
  onClose: () => void;
  t: LanguageContextType['t'];
}

const AttachmentPreviewPanel: React.FC<AttachmentPreviewPanelProps> = ({ item, onClose, t }) => {
  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(0);

  if (!item || !Array.isArray(item.attachments) || item.attachments.length === 0) {
    return null; 
  }

  const currentAttachment = item.attachments[currentAttachmentIndex];
  const canPreviewDirectly = AttachmentManager.canPreviewImage(currentAttachment) || (currentAttachment.type === 'application/pdf' && currentAttachment.url);

  const handleNextAttachment = () => setCurrentAttachmentIndex(prev => (prev + 1) % item.attachments.length);
  const handlePrevAttachment = () => setCurrentAttachmentIndex(prev => (prev - 1 + item.attachments.length) % item.attachments.length);

  return (
    <div className="attachment-preview-overlay">
      <div className="attachment-preview-modal">
        {/* Left Column: Preview Area (70%) */}
        <div className="attachment-preview-display">
          <div className="attachment-preview-content">
            {canPreviewDirectly ? (
              AttachmentManager.canPreviewImage(currentAttachment) ? (
                <img 
                  src={currentAttachment.url || currentAttachment.data} 
                  alt={currentAttachment.name} 
                  className="attachment-preview-image" 
                />
              ) : currentAttachment.type === 'application/pdf' && currentAttachment.url ? (
                <iframe 
                  src={currentAttachment.url} 
                  className="attachment-preview-iframe" 
                  title={currentAttachment.name}
                ></iframe>
              ) : null
            ) : (
              <div className="attachment-preview-no-preview">
                <FileText className="attachment-preview-no-preview-icon" />
                <p className="attachment-preview-no-preview-title">{currentAttachment.name}</p>
                <p className="attachment-preview-no-preview-text">{t('attachment.no.preview', { defaultValue: 'Preview not available for this file type.' })}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Controls and Details (30%) */}
        <div className="attachment-preview-sidebar">
          {/* Top: Close button and navigation */}
          <div className="attachment-preview-header">
            <h3 className="attachment-preview-title">Attachments</h3>
            <button onClick={onClose} className="attachment-preview-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation arrows for multiple attachments */}
          {item.attachments.length > 1 && (
            <div className="attachment-preview-navigation">
              <button onClick={handlePrevAttachment} className="attachment-nav-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Previous
              </button>
              <span className="attachment-nav-counter">
                {currentAttachmentIndex + 1} / {item.attachments.length}
              </span>
              <button onClick={handleNextAttachment} className="attachment-nav-btn">
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          )}
          
          {/* Current attachment details */}
          <div className="attachment-preview-details">
            <p className="attachment-details-name" title={currentAttachment.name}>
              {currentAttachment.name}
            </p>
            <p className="attachment-details-info">
              {currentAttachment.type} • {AttachmentManager.formatFileSize(currentAttachment.size)}
            </p>
            
            {/* Download button */}
            {(currentAttachment.url || AttachmentManager.getDownloadUrl(currentAttachment)) && (
              <a
                href={currentAttachment.url || AttachmentManager.getDownloadUrl(currentAttachment)}
                download={currentAttachment.name}
                target="_blank"
                rel="noopener noreferrer"
                className="attachment-download-btn"
              >
                <Download className="w-4 h-4 mr-2" />{t('action.download')}
              </a>
            )}
          </div>

          {/* List of all attachments */}
          <div className="attachment-preview-list-container">
            <p className="attachment-list-title">
              {t('general.files')} ({Array.isArray(item.attachments) ? item.attachments.length : 0})
            </p>
            <div className="attachment-preview-list custom-scrollbar">
              {(Array.isArray(item.attachments) ? item.attachments : []).map((att, index) => (
                <button
                  key={att.id}
                  onClick={() => setCurrentAttachmentIndex(index)}
                  title={att.name}
                  className={`attachment-list-item ${
                    index === currentAttachmentIndex ? 'attachment-list-item-active' : ''
                  }`}
                >
                  <div className="attachment-list-item-content">
                    <span className="attachment-list-item-number">
                      {(index + 1).toString().padStart(2, '0')}
                    </span>
                    <span className="attachment-list-item-name">{att.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentPreviewPanel;